import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Container, Typography, FormControl, InputLabel, Select, MenuItem, InputAdornment, IconButton, CircularProgress, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Close as CloseIcon, ChevronRight, ExpandMore, Folder as FolderIcon, Description, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FileUploadModal from './fileupload';
import { useSnackbar } from '../SnackbarContext';
import axios from 'axios';
import GlobalVariable from '../../services/Global';
import EYEIMG from '../../assets/Images/eye.svg';
import Trash_Empty from '../../assets/Images/Trash_Empty.svg';
import './WinningRfp.css';

const baseApiUrl = GlobalVariable.BASE_API_URL;

const WinningRfp = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [rfpType, setRfpType] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [winningRfps, setWinningRfps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState({ url: null, fileName: null });

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleRfpTypeChange = (event) => {
    const newType = event.target.value;
    setRfpType(newType);
    fetchWinningRfps(newType); // Fetch data when type changes
  };

  const handleClearRfpType = () => {
    setRfpType('');
    fetchWinningRfps(''); // Fetch all when cleared (empty string defaults to 'all' in API)
  };

  const fetchWinningRfps = useCallback(async (type = rfpType) => {
    try {
      setLoading(true);
      const storedData = JSON.parse(localStorage.getItem('userDetails'));
      const accessToken = storedData?.access_token;
      
      // Add rfp_type as query parameter
      const params = new URLSearchParams({
        rfp_type: type || 'all'
      });
      
      const response = await axios.get(`${baseApiUrl}/winning_rfp/get_winning_rfps?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (response.data.status_code === 200) {
        // Extract all files from each winning RFP
        const processedRfps = [];
        const winningRfpsData = response.data.winning_rfps || response.data.results || [];
        
        winningRfpsData.forEach((rfp) => {
          const uploadedBy = rfp.uploaded_by_name 
            || rfp.uploaded_by 
            || rfp.uploadedUser 
            || rfp.uploaded_user 
            || rfp.user_name 
            || rfp.userName 
            || rfp.created_by 
            || rfp.createdBy 
            || rfp.owner_name 
            || rfp.ownerName 
            || '';
          const allFiles = rfp.allFiles || [];
          const rfpType = rfp.rfp_type || 'N/A';
          const rfpId = rfp.rfp_id || rfp._id || rfp.id;
          const folderName = rfp.folder_name;
          // Check if folderName indicates a single file upload folder (e.g., "single_file_upload", "Uploaded_Folder" with 1 file)
          // Single file uploads via "Browse Files" should always be standalone
          const isSingleFileUploadFolder = folderName && (
            folderName.toLowerCase().includes('single_file') || 
            (folderName === 'Uploaded_Folder' && allFiles.length === 1)
          );
          // Treat as folder if folderName exists AND it's not a single file upload folder
          // Folders originally uploaded via "Browse Folder" will have folderName and multiple files (or 1 after deletions)
          const isFolderUpload = !!folderName && !isSingleFileUploadFolder && allFiles.length > 0;
          
          if (isFolderUpload) {
            // Group files by folder (treats as folder even if only one file remains after deletions)
            const folderFiles = allFiles.map((fileUrl) => {
              const urlParts = fileUrl.split('/');
              const fileName = decodeURIComponent(urlParts[urlParts.length - 1]);
              return {
                id: `${rfpId}_${fileUrl}`,
                fileUrl: fileUrl,
                fileName: fileName,
                rfpType: rfpType,
                rfpId: rfpId,
                partnerName: rfp.partner_name || '',
                createdAt: rfp.created_at || '',
                uploadedBy: uploadedBy || ''
              };
            });
            
            processedRfps.push({
              id: `folder_${rfpId}`,
              type: 'folder',
              folderName: folderName,
              files: folderFiles,
              rfpType: rfpType,
              rfpId: rfpId,
              partnerName: rfp.partner_name || '',
              createdAt: rfp.created_at || '',
              uploadedBy: uploadedBy || ''
            });
          } else {
            // Single file uploads via "Browse Files" - display directly as standalone files (not as folders)
            allFiles.forEach((fileUrl) => {
              const urlParts = fileUrl.split('/');
              const fileName = decodeURIComponent(urlParts[urlParts.length - 1]);
              
              processedRfps.push({
                id: `${rfpId}_${fileUrl}`,
                type: 'file',
                fileUrl: fileUrl,
                fileName: fileName,
                rfpType: rfpType,
                rfpId: rfpId,
                partnerName: rfp.partner_name || '',
                createdAt: rfp.created_at || '',
                uploadedBy: uploadedBy || ''
              });
            });
          }
        });
        
        // Sort: folders first, then single files at the bottom
        processedRfps.sort((a, b) => {
          // Folders (type === 'folder') come first
          if (a.type === 'folder' && b.type === 'file') return -1;
          if (a.type === 'file' && b.type === 'folder') return 1;
          // If both are same type, maintain original order
          return 0;
        });
        
        setWinningRfps(processedRfps);
      }
    } catch (error) {
      console.error('Error fetching winning RFPs:', error);
      if (error.response?.status === 401) {
        showSnackbar('Session expired. Please login again.', 'error');
      } else {
        showSnackbar('Failed to fetch winning RFPs.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [rfpType, showSnackbar]);

  // Helper function to check if file is CSV or XLSX
  const isCsvOrXlsx = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'csv' || extension === 'xlsx';
  };

  // Show download confirmation dialog
  const handleDownloadFile = (docUrl, fileName) => {
    setPendingDownload({ url: docUrl, fileName: fileName });
    setDownloadModalOpen(true);
  };

  // Close download confirmation dialog
  const handleCloseDownloadModal = () => {
    setDownloadModalOpen(false);
    setPendingDownload({ url: null, fileName: null });
  };

  // Actual download function - called after confirmation
  const confirmDownloadFile = async () => {
    const { url: docUrl, fileName } = pendingDownload;
    setDownloadModalOpen(false);
    
    if (!docUrl || docUrl === 'null' || docUrl === 'undefined' || docUrl.trim() === '') {
      showSnackbar('File URL is not available. Please contact support.', 'error');
      return;
    }

    // Clean URL - this URL comes from allFiles array in the API response
    let cleanUrl = docUrl.trim();
    
    try {
      // Check if URL is already encoded, decode it first to get the raw URL
      try {
        const decoded = decodeURIComponent(cleanUrl);
        if (decoded !== cleanUrl && decoded.startsWith('http')) {
          cleanUrl = decoded;
        }
      } catch (e) {
        // URL is not encoded, use as is
      }

      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        showSnackbar('Invalid file URL format.', 'error');
        return;
      }

      // For S3 URLs, properly encode path segments
      const match = cleanUrl.match(/^(https?:\/\/)([^\/]+)(\/.*)?$/);
      if (match) {
        const protocol = match[1];
        const host = match[2];
        const path = match[3] || '';
        
        const pathSegments = path.split('/').filter(Boolean);
        const encodedSegments = pathSegments.map(segment => {
          try {
            const decoded = decodeURIComponent(segment);
            return encodeURIComponent(decoded);
          } catch {
            return encodeURIComponent(segment);
          }
        });
        
        cleanUrl = `${protocol}${host}/${encodedSegments.join('/')}`;
      }

      // Use direct download link to avoid showing filename in network tab
      // This approach doesn't make a fetch request, so it won't appear in network tab
      const link = document.createElement('a');
      link.href = cleanUrl;
      link.download = fileName || 'download';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, 100);
      
      showSnackbar('File downloaded successfully.', 'success');
    } catch (error) {
      // Ensure cleanUrl is properly formatted for fallback
      if (!cleanUrl || !cleanUrl.startsWith('http')) {
        cleanUrl = docUrl.trim();
      }
      
      // Try direct download link as fallback
      try {
        const link = document.createElement('a');
        link.href = cleanUrl;
        link.download = fileName || 'download';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        link.click();
        
        // Cleanup
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 100);
        
        showSnackbar('Download initiated. If download doesn\'t start, the file may require authentication.', 'info');
      } catch (fallbackError) {
        // Last resort: open in new window
        try {
          window.open(cleanUrl, '_blank');
          showSnackbar('Opening file in new window. Please save it manually.', 'info');
        } catch (windowError) {
          showSnackbar('Unable to download file. Please check S3 bucket CORS settings or contact support.', 'error');
        }
      }
    }
  };

  const handleViewDocument = (docUrl, fileName) => {
    if (!docUrl || docUrl === 'null' || docUrl === 'undefined' || docUrl.trim() === '') {
      showSnackbar('Document URL is not available. Please contact support.', 'error');
      return;
    }

    try {
      // Clean URL
      let cleanUrl = docUrl.trim();
      
      // Check if URL is already encoded, decode it first to get the raw URL
      try {
        const decoded = decodeURIComponent(cleanUrl);
        if (decoded !== cleanUrl && decoded.startsWith('http')) {
          cleanUrl = decoded;
        }
      } catch (e) {
        // URL is not encoded, use as is
      }

      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        showSnackbar('Invalid document URL format.', 'error');
        return;
      }

      // For S3 URLs, always properly encode path segments to handle spaces and special characters
      // Parse URL manually to encode each path segment
      const match = cleanUrl.match(/^(https?:\/\/)([^\/]+)(\/.*)?$/);
      if (match) {
        const protocol = match[1];
        const host = match[2];
        const path = match[3] || '';
        
        // Split path and encode each segment properly
        // This handles spaces (like "document 3") and special characters (#, parentheses, etc.)
        // But preserves valid URL characters like underscores, hyphens, and dots
        const pathSegments = path.split('/').filter(Boolean);
        const encodedSegments = pathSegments.map(segment => {
          try {
            // First decode in case it's partially encoded
            const decoded = decodeURIComponent(segment);
            
            // Only encode characters that need encoding (spaces, special chars)
            // But preserve valid URL characters like _, -, ., etc.
            return decoded.split('').map(char => {
              // Characters that are safe in URLs and shouldn't be encoded
              if (/[a-zA-Z0-9._-]/.test(char)) {
                return char;
              }
              // Encode everything else
              return encodeURIComponent(char);
            }).join('');
          } catch {
            // If decode fails, check if it's already properly encoded
            if (segment.includes('%') && /^[%a-zA-Z0-9._-]+$/.test(segment.replace(/%[0-9A-Fa-f]{2}/g, ''))) {
              return segment; // Already encoded, use as-is
            }
            // Otherwise, encode only what needs encoding
            return segment.split('').map(char => {
              if (/[a-zA-Z0-9._-]/.test(char)) {
                return char;
              }
              return encodeURIComponent(char);
            }).join('');
          }
        });
        
        // Reconstruct the URL with properly encoded path segments
        cleanUrl = `${protocol}${host}/${encodedSegments.join('/')}`;
        
        // Validate the properly encoded URL
        try {
          new URL(cleanUrl);
        } catch (urlError) {
          console.error('URL validation failed after encoding:', urlError, cleanUrl);
          // Still proceed - the encoding should be correct
        }
      } else {
        // If we can't parse, try to validate as-is
        try {
          new URL(cleanUrl);
        } catch (urlError) {
          console.error('Invalid URL format:', urlError, cleanUrl);
          showSnackbar('Invalid document URL format.', 'error');
          return;
        }
      }
      
      // Navigate to document viewer - encode the entire URL for the route parameter
      const encodedUrl = encodeURIComponent(cleanUrl);
      navigate(`/view-document/${encodedUrl}`, { state: { fileName: fileName } });
    } catch (error) {
      console.error('Invalid document URL:', error, docUrl);
      showSnackbar('Invalid document URL. Please contact support.', 'error');
    }
  };

  const handleDeleteFile = (rfpId, fileName) => {
    setDeleteItem({
      type: 'folder',
      rfpId: rfpId,
      fileName: fileName,
      fileUrl: null
    });
    setDeleteModalOpen(true);
  };

  const confirmDeleteFile = async () => {
    if (!deleteItem) return;

    try {
      const storedData = JSON.parse(localStorage.getItem('userDetails'));
      const accessToken = storedData?.access_token;
      
      let response;
      
      // Use different API endpoints based on deletion type
      if (deleteItem.type === 'folder') {
        // For folder deletion, use delete_winning_rfp API with rfp_id in body
        response = await axios.delete(`${baseApiUrl}/winning_rfp/delete_winning_rfp`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          data: {
            rfp_id: deleteItem.rfpId,
          },
        });
      } else if (deleteItem.isStandalone) {
        // For standalone files (uploaded via "browse file"), use delete_winning_rfp API
        response = await axios.delete(`${baseApiUrl}/winning_rfp/delete_winning_rfp`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          data: {
            rfp_id: deleteItem.rfpId,
          },
        });
      } else {
        // For files inside folders, use delete_file API
        const params = new URLSearchParams({
          rfp_id: deleteItem.rfpId,
        });

        // Add file_url only if it's an individual file deletion
        if (deleteItem.fileUrl) {
          params.append('file_url', deleteItem.fileUrl);
        }
        
        response = await axios.delete(`${baseApiUrl}/winning_rfp/delete_file?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
      
      if (response.data.status_code === 200) {
        const message = deleteItem.type === 'folder' ? 'Folder deleted successfully.' : 'File deleted successfully.';
        showSnackbar(message, 'success');
        // Small delay to ensure backend has processed the deletion
        setTimeout(() => {
          fetchWinningRfps(); // Refresh the list
        }, 500);
      } else {
        const errorMessage = deleteItem.type === 'folder' ? 'Failed to delete folder.' : 'Failed to delete file.';
        showSnackbar(errorMessage || 'Delete operation failed.', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      if (error.response?.status === 401) {
        showSnackbar('Session expired. Please login again.', 'error');
      } else if (error.response?.data?.message) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        const errorMessage = deleteItem.type === 'folder' ? 'Failed to delete folder.' : 'Failed to delete file.';
        showSnackbar(errorMessage, 'error');
      }
    } finally {
      setDeleteModalOpen(false);
      setDeleteItem(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItem(null);
  };

  const handleDeleteIndividualFile = (rfpId, fileUrl, fileName, isStandalone = false) => {
    setDeleteItem({
      type: 'file',
      rfpId: rfpId,
      fileName: fileName,
      fileUrl: fileUrl,
      isStandalone: isStandalone // Flag to identify standalone files (uploaded via "browse file")
    });
    setDeleteModalOpen(true);
  };

  const handleToggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // Only fetch on component mount
  useEffect(() => {
    fetchWinningRfps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth={false} className="winning-rfp-container" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
      <Box className="winning-rfp-header" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' }
      }}>
        <Typography variant="h4" component="h1" className="winning-rfp-title" sx={{ fontWeight: 600 }}>
          Winning RFP'S
        </Typography>
        <Box className="winning-rfp-header-actions" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
          width: { xs: '100%', md: 'auto' },
          alignItems: { xs: 'stretch', md: 'center' }
        }}>
          <FormControl size="small" className='inputfields' sx={{ 
            minWidth: { xs: '100%', md: 180 },
            width: { xs: '100%', md: 'auto' }
          }}>
         
            <InputLabel sx={{ fontSize: '12px' }} className='fontstyleet' id="rfp-type-select-label">RFP Type</InputLabel>
            <Select
              labelId="rfp-type-select-label"
              className='inputfields rounded-3'
              value={rfpType}
              onChange={handleRfpTypeChange}
              label="RFP Type"
              MenuProps={{
                PaperProps: {
                  style: {
                    marginTop: '6px',
                    borderRadius: '10px',
                  },
                },
              }}
              IconComponent={rfpType ? () => null : undefined}
              endAdornment={rfpType && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearRfpType}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              )}
            >
              <MenuItem value='solutioning'>Solutioning</MenuItem>
              <MenuItem value='staffing'>Staffing</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleOpenModal}
            className="bg-primary text-capitalize rounded-3"
            sx={{ 
              px: 3,
              width: { xs: '100%', md: 'auto' }
            }}
          >
            Upload Files
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box className="winning-rfp-loading" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ mt: 4 }}>
          <TableContainer className="winning-rfp-table-container" sx={{
            width: '100%',
            height: 'auto',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              height: '3px',
              width: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
              cursor: 'pointer',
            },
          }}>
            <Table className="winning-rfp-table" aria-label="winning rfps table" stickyHeader sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                    <Typography className='fontstylee'>Document Name</Typography>
                  </TableCell>
                  <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                    <Typography className='fontstylee'>Uploaded By</Typography>
                  </TableCell>
                  <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                    <Typography className='fontstylee'>RFP Type</Typography>
                  </TableCell>
                  <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                    <Typography className='fontstylee'>Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className='position-relative'>
                {winningRfps.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box className="winning-rfp-empty-state" sx={{ 
                        p: 4, 
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ color: '#666' }}>
                          No files uploaded yet
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  winningRfps.map((item, index) => {
                    if (item.type === 'folder') {
                      // Render folder with expand/collapse
                      const isExpanded = expandedFolders[item.id] || false;
                      const fileCount = item.files?.length || 0;
                      const folderUploader = item.uploadedBy && item.uploadedBy.trim() !== '' ? item.uploadedBy : 'N/A';
                      
                      return (
                        <React.Fragment key={item.id}>
                          <TableRow 
                            className="winning-rfp-row"
                            sx={{ 
                              backgroundColor: isExpanded ? '#f5f5f5' : '#fff',
                              '&:hover': { backgroundColor: '#f9f9f9' },
                              cursor: 'pointer'
                            }}
                            onClick={() => handleToggleFolder(item.id)}
                          >
                            <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                              <Box className="winning-rfp-folder-content" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton className="winning-rfp-icon-button" size="small" sx={{ p: 0.5 }}>
                                  {isExpanded ? <ExpandMore /> : <ChevronRight />}
                                </IconButton>
                                <FolderIcon sx={{ color: '#1976d2', mr: 1, fontSize: 20 }} />
                                <Typography className="winning-rfp-folder-name" variant="body1" sx={{ fontWeight: 600 }}>
                                  {item.folderName}
                                </Typography>
                                <Typography className="winning-rfp-file-count" variant="caption" sx={{ color: '#666', ml: 1 }}>
                                  ({fileCount} {fileCount === 1 ? 'file' : 'files'})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                              <Typography variant="body2">
                                {folderUploader}
                              </Typography>
                            </TableCell>
                            <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {item.rfpType || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                              <Box className="winning-rfp-actions">
                                <Tooltip title="Delete Folder" arrow>
                                  <IconButton 
                                    className="winning-rfp-icon-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFile(item.rfpId, item.folderName);
                                    }}
                                  >
                                    <img 
                                      src={Trash_Empty} 
                                      className="winning-rfp-icon-large"
                                      width="31px" 
                                      height="31px" 
                                      alt="trshimg" 
                                      style={{ 
                                        backgroundColor: '#FCD9D9', 
                                        borderRadius: '38%', 
                                        padding: '7px' 
                                      }} 
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          {item.files?.map((file) => {
                            const docUrl = file.fileUrl;
                            const hasValidUrl = docUrl && docUrl !== 'null' && docUrl !== 'undefined' && docUrl.trim() !== '';
                            const fileName = file.fileName;
                            const uploaderName = (file.uploadedBy && file.uploadedBy.trim() !== '' 
                              ? file.uploadedBy 
                              : (folderUploader !== 'N/A' ? folderUploader : 'N/A'));
                            
                            return (
                              <TableRow key={file.id}>
                                <TableCell colSpan={4} sx={{ py: 0, border: 'none', p: 0 }}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box className="winning-rfp-nested-file" sx={{ 
                                      display: 'grid',
                                      gridTemplateColumns: '1fr minmax(150px, 0.4fr) auto auto',
                                      width: '100%', 
                                      backgroundColor: '#fafafa',
                                      alignItems: 'center'
                                    }}>
                                      <Box className="winning-rfp-nested-file-content" sx={{ 
                                        pl: 6, 
                                        py: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                        overflow: 'hidden'
                                      }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                          <Description sx={{ color: '#666', fontSize: 18, flexShrink: 0 }} />
                                          <Typography className="winning-rfp-file-name" variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {fileName}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ 
                                        py: 1.5,
                                        px: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        <Typography variant="body2">
                                          {uploaderName}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ 
                                        py: 1.5,
                                        px: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                          {file.rfpType || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box className="winning-rfp-actions" sx={{ 
                                        py: 1.5,
                                        px: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 1,
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {isCsvOrXlsx(fileName) ? (
                                          <Tooltip title={!hasValidUrl ? 'File URL not available' : 'Download File'} arrow>
                                            <IconButton 
                                              className="winning-rfp-icon-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadFile(docUrl, fileName);
                                              }}
                                              disabled={!hasValidUrl}
                                              size="small"
                                              sx={{
                                                opacity: !hasValidUrl ? 0.5 : 1
                                              }}
                                            >
                                              <Download 
                                                sx={{ 
                                                  color: '#1976d2', 
                                                  fontSize: 20,
                                                  backgroundColor: '#D9EAFF', 
                                                  borderRadius: '38%', 
                                                  padding: '5px'
                                                }} 
                                              />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title={!hasValidUrl ? 'Document URL not available' : 'View Document'} arrow>
                                            <IconButton 
                                              className="winning-rfp-icon-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewDocument(docUrl, fileName);
                                              }}
                                              disabled={!hasValidUrl}
                                              size="small"
                                              sx={{
                                                opacity: !hasValidUrl ? 0.5 : 1
                                              }}
                                            >
                                              <img 
                                                src={EYEIMG} 
                                                className="winning-rfp-icon"
                                                width="24px" 
                                                height="24px" 
                                                alt="eyeimg" 
                                                style={{ 
                                                  backgroundColor: '#D9EAFF', 
                                                  borderRadius: '38%', 
                                                  padding: '5px'
                                                }} 
                                              />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <Tooltip title="Delete File" arrow>
                                          <IconButton 
                                            className="winning-rfp-icon-button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteIndividualFile(file.rfpId, file.fileUrl, fileName);
                                            }}
                                            size="small"
                                          >
                                            <img 
                                              src={Trash_Empty} 
                                              className="winning-rfp-icon"
                                              width="24px" 
                                              height="24px" 
                                              alt="trshimg" 
                                              style={{ 
                                                backgroundColor: '#FCD9D9', 
                                                borderRadius: '38%', 
                                                padding: '5px'
                                              }} 
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    } else {
                      // Render single file normally
                      const docUrl = item.fileUrl;
                      const hasValidUrl = docUrl && docUrl !== 'null' && docUrl !== 'undefined' && docUrl.trim() !== '';
                      const fileName = item.fileName || `File ${index + 1}`;
                      const rfpTypeValue = item.rfpType || 'N/A';
                      const uploaderDisplay = item.uploadedBy && item.uploadedBy.trim() !== '' ? item.uploadedBy : 'N/A';

                      return (
                        <TableRow key={item.id || index} className="winning-rfp-row">
                          <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                            <Box className="winning-rfp-folder-content" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Description sx={{ color: '#666', fontSize: 18 }} />
                              <Typography className="winning-rfp-file-name" variant="body1" sx={{ fontWeight: 500 }}>
                                {fileName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">
                              {uploaderDisplay}
                            </Typography>
                          </TableCell>
                          <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {rfpTypeValue}
                            </Typography>
                          </TableCell>
                          <TableCell className="winning-rfp-table-cell" sx={{ width: 'auto', whiteSpace: 'nowrap' }}>
                            <Box className="winning-rfp-actions" sx={{ display: 'flex', gap: 1 }}>
                              {isCsvOrXlsx(fileName) ? (
                                <Tooltip title={!hasValidUrl ? 'File URL not available' : 'Download File'} arrow>
                                  <IconButton 
                                    className="winning-rfp-icon-button"
                                    onClick={() => handleDownloadFile(docUrl, fileName)}
                                    disabled={!hasValidUrl}
                                    sx={{
                                      opacity: !hasValidUrl ? 0.5 : 1
                                    }}
                                  >
                                    <Download 
                                      sx={{ 
                                        color: '#1976d2', 
                                        fontSize: 24,
                                        backgroundColor: '#D9EAFF', 
                                        borderRadius: '38%', 
                                        padding: '7px'
                                      }} 
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title={!hasValidUrl ? 'Document URL not available' : 'View Document'} arrow>
                                  <IconButton 
                                    className="winning-rfp-icon-button"
                                    onClick={() => handleViewDocument(docUrl, fileName)}
                                    disabled={!hasValidUrl}
                                    sx={{
                                      opacity: !hasValidUrl ? 0.5 : 1
                                    }}
                                  >
                                    <img 
                                      src={EYEIMG} 
                                      className="winning-rfp-icon-large"
                                      width="31px" 
                                      height="31px" 
                                      alt="eyeimg" 
                                      style={{ 
                                        backgroundColor: '#D9EAFF', 
                                        borderRadius: '38%', 
                                        padding: '7px'
                                      }} 
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete Document" arrow>
                                <IconButton 
                                  className="winning-rfp-icon-button"
                                  onClick={() => handleDeleteIndividualFile(item.rfpId, item.fileUrl, fileName, true)}
                                >
                                  <img 
                                    src={Trash_Empty} 
                                    className="winning-rfp-icon-large"
                                    width="31px" 
                                    height="31px" 
                                    alt="trshimg" 
                                    style={{ 
                                      backgroundColor: '#FCD9D9', 
                                      borderRadius: '38%', 
                                      padding: '7px' 
                                    }} 
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <FileUploadModal open={modalOpen} onClose={handleCloseModal} rfpType={rfpType} partnerName={partnerName} onUploadSuccess={fetchWinningRfps} />

      {/* Download Confirmation Modal */}
      <Dialog
        open={downloadModalOpen}
        onClose={handleCloseDownloadModal}
        aria-labelledby="download-dialog-title"
        aria-describedby="download-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle id="download-dialog-title" sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Download Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="download-dialog-description" sx={{ fontSize: '14px', color: '#666' }}>
            Are you sure you want to download <strong>{pendingDownload.fileName || 'this file'}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDownloadModal}
            variant="outlined"
            sx={{
              textTransform: 'capitalize',
              borderRadius: '8px',
              px: 3,
              borderColor: '#ddd',
              color: '#666',
              '&:hover': {
                borderColor: '#bbb',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDownloadFile}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'capitalize',
              borderRadius: '8px',
              px: 3,
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          className: "winning-rfp-delete-modal",
          sx: {
            borderRadius: '12px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle id="delete-dialog-title" className="winning-rfp-delete-modal-content" sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent className="winning-rfp-delete-modal-content">
          <DialogContentText id="delete-dialog-description" sx={{ fontSize: '14px', color: '#666' }}>
            Are you sure you want to delete <strong>{deleteItem?.fileName || 'this item'}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="winning-rfp-delete-modal-actions" sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteModal}
            variant="outlined"
            sx={{
              textTransform: 'capitalize',
              borderRadius: '8px',
              px: 3,
              borderColor: '#ddd',
              color: '#666',
              '&:hover': {
                borderColor: '#bbb',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteFile}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'capitalize',
              borderRadius: '8px',
              px: 3,
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WinningRfp;

